import React, { useState } from "react";
import { Checkbox, Field, Text } from "@fluentui/react-components";
import { useParams } from "react-router-dom";
import {
  type ConnectionEditState,
  type ConnectionEditStateUpdater,
  type Status,
  type SourceStatusMapping,
} from "@onetasklist/shared";
import {
  useClickUpSourceSettingsText,
  useClickUpSourceSettingsViewModel,
} from "@onetasklist/shared/sources/clickup";
import { getSourceSettingsApiAccessor } from "@onetasklist/shared/sources/source-settings";
import { useApi } from "@api/useApi";
import { useTranslation } from "react-i18next";
import { SourceMultiTagPicker } from "./components/SourceMultiTagPicker";
import { SourceSingleTagPicker } from "./components/SourceSingleTagPicker";
import { SettingsSectionCard } from "./components/SettingsSectionCard";
import { StatusMappingSection } from "./components/StatusMappingSection";

interface ClickUpSourceSettingsProps {
  editingConnection: ConnectionEditState;
  onUpdate: ConnectionEditStateUpdater;
  sectionOffset: number;
  boardStatuses: Status[];
}

export const ClickUpSourceSettings: React.FC<ClickUpSourceSettingsProps> = ({
  editingConnection,
  onUpdate,
  sectionOffset,
  boardStatuses,
}) => {
  const { t } = useTranslation();
  const { boardId } = useParams<{ boardId?: string }>();
  const api = useApi();
  const text = useClickUpSourceSettingsText(t);
  const vm = useClickUpSourceSettingsViewModel({
    boardId,
    editingConnection,
    onUpdate,
    sourceSettingsApi: getSourceSettingsApiAccessor(api),
    t,
  });

  const [workspaceQuery, setWorkspaceQuery] = useState("");
  const [spaceQuery, setSpaceQuery] = useState("");
  const [folderQuery, setFolderQuery] = useState("");
  const [listQuery, setListQuery] = useState("");
  const [statusesQuery, setStatusesQuery] = useState("");
  const [doneStatusQuery, setDoneStatusQuery] = useState("");

  if (!vm.isVisible) {
    return null;
  }

  return (
    <>
      <SettingsSectionCard
        sectionOffset={sectionOffset}
        title={t(
          "board.connections.catalogue.sourceSettings.clickup.sectionWorkspace",
        )}
      >
        <Field
          orientation="horizontal"
          label={text.workspaceLabelText}
          hint={text.workspaceHintText}
        >
          <SourceSingleTagPicker
            selectedId={
              vm.selectedWorkspaceId === vm.noneOptionValue
                ? undefined
                : vm.selectedWorkspaceId
            }
            selectedLabel={vm.selectedWorkspaceName}
            query={workspaceQuery}
            placeholder={text.workspacePlaceholderText}
            options={vm.workspaces.map((workspace) => ({
              id: workspace.id,
              label: workspace.name || workspace.id,
            }))}
            onQueryChange={setWorkspaceQuery}
            onSelect={vm.onSelectWorkspace}
            clearSelectionText={text.workspacePlaceholderText}
            isLoading={!vm.workspacesLoaded}
          />
        </Field>

        {vm.selectedWorkspaceId !== vm.noneOptionValue && (
          <Field
            orientation="horizontal"
            label={text.spaceLabelText}
            hint={text.spaceHintText}
          >
            <SourceSingleTagPicker
              selectedId={
                vm.selectedSpaceId === vm.noneOptionValue
                  ? undefined
                  : vm.selectedSpaceId
              }
              selectedLabel={vm.selectedSpaceName}
              query={spaceQuery}
              placeholder={text.spacePlaceholderText}
              options={vm.spaces.map((space) => ({
                id: space.id,
                label: space.name || space.id,
              }))}
              onQueryChange={setSpaceQuery}
              onSelect={vm.onSelectSpace}
              clearSelectionText={text.spacePlaceholderText}
              isLoading={!vm.spacesLoaded}
            />
          </Field>
        )}

        {vm.selectedSpaceId !== vm.noneOptionValue && (
          <>
            <Field
              orientation="horizontal"
              label={text.folderLabelText}
              hint={text.folderHintText}
            >
              <SourceSingleTagPicker
                selectedId={
                  vm.selectedFolderId === vm.noneOptionValue
                    ? undefined
                    : vm.selectedFolderId
                }
                selectedLabel={vm.selectedFolderName}
                query={folderQuery}
                placeholder={text.folderPlaceholderText}
                options={vm.folders.map((folder) => ({
                  id: folder.id,
                  label: folder.name || folder.id,
                }))}
                onQueryChange={setFolderQuery}
                onSelect={vm.onSelectFolder}
                clearSelectionText={text.folderPlaceholderText}
                isLoading={!vm.foldersLoaded}
              />
            </Field>

            <Field
              orientation="horizontal"
              label={text.listLabelText}
              hint={text.listHintText}
            >
              <SourceSingleTagPicker
                selectedId={
                  vm.selectedListId === vm.noneOptionValue
                    ? undefined
                    : vm.selectedListId
                }
                selectedLabel={vm.selectedListName}
                query={listQuery}
                placeholder={text.listPlaceholderText}
                options={vm.lists.map((list) => ({
                  id: list.id,
                  label: list.name || list.id,
                }))}
                onQueryChange={setListQuery}
                onSelect={vm.onSelectList}
                clearSelectionText={text.listPlaceholderText}
                isLoading={!vm.listsLoaded}
              />
            </Field>
          </>
        )}
      </SettingsSectionCard>

      {vm.selectedListId !== vm.noneOptionValue && (
        <SettingsSectionCard
          sectionOffset={sectionOffset + 1}
          title={t(
            "board.connections.catalogue.sourceSettings.clickup.sectionStatuses",
          )}
        >
          <Field
            orientation="horizontal"
            label={text.statusesLabelText}
            hint={text.statusesHintText}
          >
            <SourceMultiTagPicker
              selectedIds={vm.selectedStatusIds}
              query={statusesQuery}
              placeholder={text.statusesPlaceholderText}
              options={vm.statuses.map((status) => ({
                id: status.id,
                label: status.status || status.id,
              }))}
              onQueryChange={setStatusesQuery}
              onSelectionChange={vm.onSelectStatuses}
              minQueryLength={0}
              typeToSearchText={text.statusesHintText}
              isLoading={!vm.statusesLoaded}
            />
          </Field>

          <Field
            orientation="horizontal"
            label={text.doneStatusLabelText}
            hint={text.doneStatusHintText}
          >
            <SourceSingleTagPicker
              selectedId={
                vm.selectedDoneStatusId === vm.noneOptionValue
                  ? undefined
                  : vm.selectedDoneStatusId
              }
              selectedLabel={vm.selectedDoneStatusName}
              query={doneStatusQuery}
              placeholder={text.doneStatusPlaceholderText}
              options={vm.statuses.map((status) => ({
                id: status.id,
                label: status.status || status.id,
              }))}
              onQueryChange={setDoneStatusQuery}
              onSelect={vm.onSelectDoneStatus}
              clearSelectionText={text.doneStatusPlaceholderText}
              isLoading={!vm.statusesLoaded}
            />
          </Field>

          <Field orientation="horizontal" label={text.assignedToMeLabelText}>
            <Checkbox
              checked={vm.assignedToMe}
              onChange={(_, data) =>
                vm.onToggleAssignedToMe(Boolean(data.checked))
              }
            />
          </Field>
        </SettingsSectionCard>
      )}

      {!vm.workspacesLoaded && <Text>{text.loadingWorkspacesText}</Text>}
      {vm.workspacesLoaded &&
        !vm.spacesLoaded &&
        vm.selectedWorkspaceId !== vm.noneOptionValue && (
          <Text>{text.loadingSpacesText}</Text>
        )}
      {vm.spacesLoaded &&
        !vm.foldersLoaded &&
        vm.selectedSpaceId !== vm.noneOptionValue && (
          <Text>{text.loadingFoldersText}</Text>
        )}
      {vm.foldersLoaded &&
        !vm.listsLoaded &&
        vm.selectedSpaceId !== vm.noneOptionValue && (
          <Text>{text.loadingListsText}</Text>
        )}
      {vm.listsLoaded &&
        !vm.statusesLoaded &&
        vm.selectedListId !== vm.noneOptionValue && (
          <Text>{text.loadingStatusesText}</Text>
        )}
      {vm.hasError && <Text>{text.loadErrorText}</Text>}

      {vm.selectedListId !== vm.noneOptionValue &&
        vm.selectedStatusIds.length > 0 && (
          <StatusMappingSection
            sectionNumber={sectionOffset + 1}
            items={vm.statuses
              .filter((s) => vm.selectedStatusIds.includes(s.id))
              .map((s) => ({ id: s.id, name: s.status || s.id }))}
            mappings={
              ((editingConnection.configuration as Record<string, unknown>)
                ?.statusMappings as SourceStatusMapping[] | undefined) ?? []
            }
            boardStatuses={boardStatuses}
            onChange={(mappings) =>
              onUpdate({
                configuration: {
                  ...(editingConnection.configuration as Record<string, unknown>),
                  statusMappings: mappings,
                },
              })
            }
          />
        )}
    </>
  );
};
